import moment from 'moment';
import { dateFormatter } from 'helpers/dateFormatter';
import { STEPS_LABELS, stepsStatuses } from 'constants/constants';
import { OPENING_STATUSES } from 'constants/opening';
import type { DashboardOpeningsGroupRow } from '_templates/dashboardTypes';
import { BUTTON_LABELS, OPENINGS_GROUP } from './constants';

export const isServiceGrouping = (grouping: string) => grouping === OPENINGS_GROUP[0].value;

export const serviceTableDataMapper = (services: DashboardOpeningsGroupRow[]) =>
  services.map(service => ({
    uuid: service.uuid,
    name: service.name,
    count: service.openings_count,
    candidates_count: service.candidates_count,
    openings_count: service.total_openings_count,
    openings: service.openings.map(opening => ({
      uuid: opening.uuid,
      job: {
        job_name: opening.job_name,
        job_uuid: opening.job_uuid,
        priority: opening.priority,
      },
      level: opening.custom_level || opening.level,
      location: (opening.locations ?? []).join(', ') || '-',
      creation_date: dateFormatter(opening.created),
      expected_date: {
        date: dateFormatter(opening.expected as string),
        error:
          opening.status === OPENING_STATUSES.OPEN &&
          moment(opening.expected as string, 'YYYY-MM-DD')
            .startOf('day')
            .isSameOrBefore(moment().startOf('day')),
      },
      team_lead: opening.team_lead.full_name,
      status: opening.status,
      action: {
        status: opening.status,
        job_uuid: opening.job_uuid,
        job_name: opening.job_name,
        service_uuid: opening.uuid,
        opening,
      },
    })),
  }));

export const jobTableDataMapper = (jobs: DashboardOpeningsGroupRow[]) =>
  jobs.map(job => ({
    uuid: job.uuid,
    name: job.name,
    count: job.openings_count,
    service: job.service_name,
    candidates_count: job.candidates_count,
    openings_count: job.total_openings_count,
    openings: job.openings.map(opening => ({
      uuid: opening.uuid,
      location: (opening.locations ?? []).join(', ') || '-',
      level: { priority: opening.priority, level: opening.custom_level || opening.level },
      creation_date: dateFormatter(opening.created),
      expected_date: {
        date: dateFormatter(opening.expected as string),
        error:
          opening.status === OPENING_STATUSES.OPEN &&
          moment(opening.expected as string, 'YYYY-MM-DD')
            .startOf('day')
            .isSameOrBefore(moment().startOf('day')),
      },
      team_lead: opening.team_lead.full_name,
      status: opening.status,
      action: {
        status: opening.status,
        job_uuid: job.uuid,
        job_name: job.name,
        service_uuid: opening.uuid,
        opening,
      },
    })),
  }));

const candidateActionStatuses = [
  stepsStatuses.waitingForFinalDecision,
  stepsStatuses.provideFeedback,
  stepsStatuses.scheduleInterview,
  stepsStatuses.sendOffer,
] as const;

export const candidatesTableDataMapper = (candidates: Record<string, unknown>[]) =>
  candidates.map((c, index) => ({
    uuid: (c.uuid as string) + index,
    candidate_name: {
      url: (c.assignment as Record<string, unknown> | undefined)
        ? `/jobs/${(c.assignment as Record<string, unknown>).job_uuid}/show/steps/${
            (c.assignment as Record<string, unknown>).step_uuid
          }/${c.uuid}`
        : `/candidates/${c.uuid}/show`,
      full_name_latin: c.full_name_latin,
      full_name_local: c.full_name_local,
      hot: false,
    },
    level: c.level,
    job: {
      job_name: (c.assignment as Record<string, unknown> | undefined)?.job_name,
      service: (c.assignment as Record<string, unknown> | undefined)?.service,
    },
    status_step: {
      status: (c.assignment as Record<string, unknown> | undefined)?.status,
      step:
        (c.assignment as Record<string, unknown> | undefined) &&
        `${
          Object.values(STEPS_LABELS).findIndex(l => l === (c.assignment as Record<string, unknown>).step_type) + 1
        } - ${(c.assignment as Record<string, unknown>).step_type}`,
    },
    action: (candidateActionStatuses as readonly string[]).includes(
      ((c.assignment as Record<string, unknown> | undefined)?.status as string) ?? ''
    )
      ? {
          text: '',
          subtext: '',
          time: '',
          link: '',
          button: {
            label: BUTTON_LABELS[(c.assignment as Record<string, unknown>).status as string],
            link: `/jobs/${(c.assignment as Record<string, unknown>).job_uuid}/show/steps/${
              (c.assignment as Record<string, unknown>).step_uuid
            }/${c.uuid}`,
          },
        }
      : stepsStatuses.waitForResolution === (c.assignment as Record<string, unknown> | undefined)?.status
      ? c.start_date &&
        moment(c.start_date as string, 'YYYY-MM-DD')
          .startOf('day')
          .isAfter(moment().startOf('day'))
        ? {
            text: 'Entry date:',
            subtext: moment(c.start_date as string, 'YYYY-MM-DD').format('MMM DD, YYYY'),
            time: '',
            link: '',
            button: '',
          }
        : {
            text: '',
            subtext: '',
            time: '',
            link: '',
            button: {
              label: BUTTON_LABELS[(c.assignment as Record<string, unknown>).status as string],
              link: `/jobs/${(c.assignment as Record<string, unknown>).job_uuid}/show/steps/${
                (c.assignment as Record<string, unknown>).step_uuid
              }/${c.uuid}`,
            },
          }
      : c.expected_answer_date
      ? moment(c.expected_answer_date as string, 'YYYY-MM-DD')
          .startOf('day')
          .isSameOrBefore(moment().startOf('day'))
        ? {
            text: '',
            subtext: '',
            time: '',
            link: '',
            button: {
              label: BUTTON_LABELS[(c.assignment as Record<string, unknown>).status as string],
              link: `/jobs/${(c.assignment as Record<string, unknown>).job_uuid}/show/steps/${
                (c.assignment as Record<string, unknown>).step_uuid
              }/${c.uuid}`,
            },
          }
        : {
            text: 'Expected answer date:',
            subtext: moment(c.expected_answer_date as string).format('MMM DD, YYYY'),
            time: '',
            link: '',
            button: '',
          }
      : c.interview
      ? {
          text: moment((c.interview as Record<string, unknown>).date as string).format('ddd MMM DD, YYYY'),
          subtext: `${dateFormatter(
            `${(c.interview as Record<string, unknown>).date} ${(c.interview as Record<string, unknown>).from_time}`,
            'H:mm'
          )} - ${dateFormatter(
            `${(c.interview as Record<string, unknown>).date} ${(c.interview as Record<string, unknown>).to_time}`,
            'H:mm'
          )}`,
          time: '',
          link: (c.interview as Record<string, unknown>).link,
          button: '',
        }
      : c.reminder
      ? {
          text: `Reminder: ${(c.reminder as Record<string, unknown>).type}`,
          subtext: '',
          time: moment((c.reminder as Record<string, unknown>).datetime as string).format('ddd MMM DD, YYYY [at] H:mm'),
          link: '',
          button: '',
        }
      : {
          text: '',
          subtext: '',
          time: '',
          link: '',
          button: '',
        },
  }));
